import React, { useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Video, X, Upload } from "lucide-react";
import { Button } from "./ui/button";
import VideoRecorderModal from "./VideoRecorderModal";

const PHASE_MAP = {
  queued: { label: "Queued…", pct: 5 },
  validating: { label: "Validating video…", pct: 12 },
  uploading: { label: "Uploading to storage…", pct: 25 },
  thumbnail: { label: "Generating thumbnail (FFmpeg)…", pct: 45 },
  transcribing: { label: "Transcribing audio (Whisper)…", pct: 70 },
  describing: { label: "Writing description (Claude)…", pct: 90 },
  complete: { label: "Done", pct: 100 },
};

/**
 * Shared AI-driven video uploader. Handles: client-side duration guard,
 * multipart upload to /upload/video/start, SSE progress consumption.
 * Passes { video_url, thumbnail_url, description, transcript } to onProcessed.
 */
export function AIVideoUploader({ title, onProcessed, disabled, testId = "ai-video-uploader", compact = false }) {
  const inputRef = useRef(null);
  const [processing, setProcessing] = useState(false);
  const [phase, setPhase] = useState("queued");
  const [preview, setPreview] = useState(null);
  const [duration, setDuration] = useState(0);
  const [result, setResult] = useState(null);
  const [recorderOpen, setRecorderOpen] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!title || title.trim().length < 3) {
      toast.error("Please enter a title (at least 3 characters) first.");
      document.querySelector('[data-testid="create-title-input"]')?.focus();
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (!file.type.startsWith("video/")) { toast.error("Please upload a valid video file (MP4, MOV, or WEBM)."); return; }

    const videoEl = document.createElement("video");
    videoEl.preload = "metadata";
    videoEl.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(videoEl.src);
      const dur = videoEl.duration;
      // MediaRecorder blobs often report `Infinity` or `NaN` for duration until
      // played through — trust the server enforcement in that case.
      if (Number.isFinite(dur) && dur > 33) {
        toast.error(`Video is ${Math.round(dur)}s long. Maximum is 30 seconds — please trim your clip.`);
        return;
      }
      setDuration(Number.isFinite(dur) ? dur : 30);
      setPreview(URL.createObjectURL(file));
      setProcessing(true);
      setPhase("queued");

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", title);
        const startRes = await api.post("/upload/video/start", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        });
        const jobId = startRes.data.job_id;
        const token = localStorage.getItem("tb_token");
        const es = new EventSource(`${API_BASE}/upload/video/progress/${jobId}?token=${token}`);

        es.addEventListener("phase", (e) => setPhase(e.data));
        es.addEventListener("complete", (e) => {
          const data = JSON.parse(e.data);
          setResult(data);
          onProcessed?.(data);
          toast.success("Video processed. Thumbnail ready.");
          setProcessing(false);
          es.close();
        });
        es.addEventListener("error", (e) => {
          let msg = "Processing failed";
          try { if (e.data) msg = JSON.parse(e.data).message || e.data; } catch (_) {}
          toast.error(msg);
          setProcessing(false);
          es.close();
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
        setProcessing(false);
      }
    };
    videoEl.src = URL.createObjectURL(file);
  };

  const clear = () => {
    setPreview(null); setResult(null); onProcessed?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const pct = PHASE_MAP[phase]?.pct ?? 0;
  const label = PHASE_MAP[phase]?.label ?? phase;

  return (
    <div data-testid={testId} className="border border-dashed border-border rounded-2xl bg-secondary/40 p-6">
      {!preview ? (
        <div className={compact ? "grid grid-cols-2 gap-3" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
          <button
            type="button"
            className={`text-center rounded-xl border border-dashed border-border ${compact ? "p-4" : "p-6"} transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-secondary/60"}`}
            onClick={() => !disabled && inputRef.current?.click()}
            disabled={disabled}
            data-testid={`${testId}-drop`}
          >
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary-soft))] mx-auto mb-2 flex items-center justify-center">
              <Upload className="w-5 h-5 text-[hsl(var(--primary))]" />
            </div>
            <p className="font-serif text-base">Upload video</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">MP4 · MOV · WEBM · Max 30s</p>
          </button>
          <button
            type="button"
            className={`text-center rounded-xl border border-dashed border-border ${compact ? "p-4" : "p-6"} transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-secondary/60"}`}
            onClick={() => !disabled && setRecorderOpen(true)}
            disabled={disabled}
            data-testid={`${testId}-record`}
          >
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--oppose))]/10 mx-auto mb-2 flex items-center justify-center">
              <Video className="w-5 h-5 text-[hsl(var(--oppose))]" />
            </div>
            <p className="font-serif text-base">Record video</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Use camera · max 30s</p>
          </button>
          <p className="col-span-full text-[11px] text-muted-foreground mt-1 flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3" /> AI auto-generates thumbnail
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative bg-black rounded-xl overflow-hidden">
            <video
              src={preview}
              controls
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v.duration === Infinity || Number.isNaN(v.duration)) {
                  const onSeeked = () => { v.currentTime = 0; v.removeEventListener("seeked", onSeeked); };
                  v.addEventListener("seeked", onSeeked);
                  try { v.currentTime = 1e10; } catch (_) { /* noop */ }
                }
              }}
              className="w-full max-h-[300px] mx-auto"
            />
            {processing && (
              <div className="absolute inset-0 bg-background/90 flex items-center justify-center">
                <div className="text-center w-full px-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))] mx-auto mb-3" />
                  <p className="font-serif text-base mb-1" data-testid={`${testId}-phase-label`}>{label}</p>
                  <p className="text-xs text-muted-foreground mb-3">{pct}%</p>
                  <div className="max-w-sm mx-auto h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--primary))] transition-all duration-500 ease-out"
                      style={{ width: `${pct}%` }}
                      data-testid={`${testId}-progress-bar`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {duration.toFixed(1)}s · {result ? "Processed" : processing ? label : "Pending"}
            </div>
            {!processing && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={clear} data-testid={`${testId}-clear`}>
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      <VideoRecorderModal
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onCaptured={(file) => { setRecorderOpen(false); handleFile(file); }}
      />
    </div>
  );
}
